/**
 * accessoires-selector.js — Chatelles
 *
 * Règles quota :
 *   0 → tout payant
 *   N → N accessoires offerts, dont 1 bijou maximum
 *       (bijou = product_type "bijoux" ; les bijoux suivants restent payants)
 *
 * Chargement via API paginée (pas de limite 50 Liquid)
 * Grille groupée par type avec titres séparateurs
 * Titres de groupe : libellé court en vue « Tous », « Paire de … » quand un
 * seul onglet de catégorie est actif
 * Double overlay pied gauche / pied droit
 */
(function () {
  'use strict';

  const COLLECTION_HANDLE = 'accessoires';

  // Ordre d'affichage des types (basés sur les tags produit) — labels via i18n
  const TYPE_ORDER = ['bijoux', 'noeuds', 'mini-noeuds', 'pompons', 'triple-pompons', 'passementeries'];

  // Titres « paire de … » affichés quand une seule catégorie est sélectionnée
  const PAIR_LABELS = {
    fr: {
      bijoux: 'Paire de bijoux',
      noeuds: 'Paire de nœuds',
      'mini-noeuds': 'Paire de mini-nœuds',
      pompons: 'Paire de pompons',
      'triple-pompons': 'Paire de triple pompons',
      passementeries: 'Paire de passementeries',
    },
    en: {
      bijoux: 'Pair of jewels',
      noeuds: 'Pair of bows',
      'mini-noeuds': 'Pair of mini bows',
      pompons: 'Pair of pompoms',
      'triple-pompons': 'Pair of triple pompoms',
      passementeries: 'Pair of passementerie',
    },
    es: {
      bijoux: 'Par de joyas',
      noeuds: 'Par de lazos',
      'mini-noeuds': 'Par de mini lazos',
      pompons: 'Par de pompones',
      'triple-pompons': 'Par de pompones triples',
      passementeries: 'Par de pasamanería',
    },
    de: {
      bijoux: 'Paar Schmuckstücke',
      noeuds: 'Paar Schleifen',
      'mini-noeuds': 'Paar Mini-Schleifen',
      pompons: 'Paar Pompons',
      'triple-pompons': 'Paar Dreifach-Pompons',
      passementeries: 'Paar Posamente',
    },
  };

  function getPairLabels() {
    const lang = (document.documentElement.lang || 'fr').toLowerCase().slice(0, 2);
    return PAIR_LABELS[lang] || PAIR_LABELS.fr;
  }

  // Groupe les produits par product_type
  function groupProducts(products) {
    const groups = {};
    TYPE_ORDER.forEach(t => { groups[t] = []; });
    products.forEach(acc => {
      const type = (acc.product_type || '').toLowerCase().trim();
      if (groups[type]) groups[type].push(acc);
    });
    return groups;
  }

  // Génère les onglets HTML en ne montrant que les catégories non vides
  function buildTabsHTML(groups, typeLabels, i18nTabAll) {
    const activeTypes = TYPE_ORDER.filter(t => groups[t] && groups[t].length > 0);
    if (activeTypes.length === 0) return '';
    let html = '';
    if (activeTypes.length > 1) {
      html += `<button class="chatelles-acc-tab" data-type="tous" role="tab" aria-selected="false">${i18nTabAll}</button>`;
    }
    activeTypes.forEach((type, i) => {
      const isFirst = i === 0;
      html += `<button class="chatelles-acc-tab${isFirst ? ' active' : ''}" data-type="${type}" role="tab" aria-selected="${isFirst ? 'true' : 'false'}">${typeLabels[type] || type}</button>`;
    });
    return html;
  }

  // ── Chargement API paginée ────────────────────────────────────────────────

  async function fetchAllProducts(handle) {
    let products = [];
    let page = 1;
    const limit = 250;
    while (true) {
      const res = await fetch(`/collections/${handle}/products.json?limit=${limit}&page=${page}`);
      if (!res.ok) break;
      const data = await res.json();
      if (!data.products || data.products.length === 0) break;
      products = products.concat(data.products);
      if (data.products.length < limit) break;
      page++;
    }
    return products;
  }

  // ── Construction de la grille groupée par type ────────────────────────────

  function buildGridHTML(groups, i18n, typeLabels, pairLabels) {
    const i18nFree = i18n.free;
    const buildItem = (acc) => {
      const variant = acc.variants && acc.variants[0];
      if (!variant) return '';
      // Disponibilité : un accessoire épuisé ferait échouer l'ajout groupé
      // (l'API /cart/add.js est atomique). On l'affiche grisé et non-sélectionnable.
      const soldOut  = variant.available === false;
      const price    = variant.price ? Math.round(parseFloat(variant.price) * 100) : 0;
      const image    = acc.images && acc.images[0];
      const imgBase  = image ? image.src.replace(/\.[^.]+$/, '') : '';
      const imgExt   = image ? image.src.match(/\.[^.]+$/)[0] : '.jpg';
      const imgThumb  = image ? `${imgBase}_120x120${imgExt}` : '';
      const imgOverlay = image ? `${imgBase}_400x400${imgExt}` : '';
      const title    = (acc.title || '').replace(/"/g, '&quot;');
      const accType  = (acc.product_type || '').toLowerCase();
      const isFree   = price === 0;
      const priceFormatted = soldOut ? i18n.soldOut : (isFree ? i18nFree : formatPrice(price));
      return `<button
        class="chatelles-acc-item${soldOut ? ' soldout' : ''}"
        data-acc-id="${acc.id}"
        data-acc-variant="${variant.id}"
        data-acc-title="${title}"
        data-acc-price="${price}"
        data-acc-type="${accType}"
        data-acc-image="${imgThumb}"
        data-acc-overlay="${imgOverlay}"
        ${soldOut ? 'data-acc-soldout="1" disabled aria-disabled="true"' : ''}
        aria-pressed="false"
        title="${soldOut ? title + ' — ' + i18n.soldOut : title}"
      >
        <div class="chatelles-acc-item-img-wrap">
          <img src="${imgThumb}" alt="${title}" loading="lazy" width="44" height="44">
          <div class="chatelles-acc-item-check">
            <svg width="10" height="8" viewBox="0 0 10 8" fill="none">
              <path d="M1 4L3.5 6.5L9 1" stroke="white" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
            </svg>
          </div>
        </div>
        <span class="chatelles-acc-item-price${soldOut ? ' soldout' : (isFree ? ' free' : '')}">${priceFormatted}</span>
      </button>`;
    };
    let html = '';
    TYPE_ORDER.forEach(type => {
      const items = groups[type];
      if (!items || items.length === 0) return;
      const shortLabel = typeLabels[type] || type;
      const pairLabel  = (pairLabels && pairLabels[type]) || shortLabel;
      html += `<div class="chatelles-acc-group" data-group="${type}">
        <h3 class="chatelles-acc-group-title" data-label-short="${esc(shortLabel)}" data-label-pair="${esc(pairLabel)}">${shortLabel}</h3>
        <div class="chatelles-acc-group-grid">
          ${items.map(buildItem).join('')}
        </div>
      </div>`;
    });
    return html || `<p class="chatelles-acc-empty">${i18n.empty}</p>`;
  }

  // ── Utilitaires ───────────────────────────────────────────────────────────

  function formatPrice(cents) {
    return (cents / 100).toLocaleString('fr-FR', {
      style: 'currency', currency: 'EUR',
      minimumFractionDigits: 0, maximumFractionDigits: 2,
    });
  }

  function esc(str) {
    return String(str).replace(/[&<>"']/g, c =>
      ({ '&':'&amp;','<':'&lt;','>':'&gt;','"':'&quot;',"'":'&#39;' }[c]));
  }

  // ── Classe principale ─────────────────────────────────────────────────────

  class AccessoiresSelector {
    constructor(wrap) {
      this.wrap = wrap;
      this.quota            = parseInt(wrap.dataset.quota || '0', 10);
      this.productVariantId = wrap.dataset.productVariant;
      this.productTitle     = wrap.dataset.productTitle;

      this.i18n = {
        tabAll:                  wrap.dataset.i18nTabAll || 'Tous',
        free:                    wrap.dataset.i18nFree || 'Offert',
        soldOut:                 wrap.dataset.i18nSoldOut || 'Épuisé',
        empty:                   wrap.dataset.i18nEmpty || 'Aucun accessoire disponible.',
        errorLoading:            wrap.dataset.i18nErrorLoading || 'Erreur de chargement.',
        remove:                  wrap.dataset.i18nRemove || 'Retirer',
        adding:                  wrap.dataset.i18nAdding || 'Ajout en cours…',
        added:                   wrap.dataset.i18nAdded || 'Ajouté !',
        errorRetry:              wrap.dataset.i18nErrorRetry || 'Erreur — réessayer',
        errorAdd:                wrap.dataset.i18nErrorAdd || 'Impossible d’ajouter au panier. Un article est peut-être épuisé — retirez-le et réessayez.',
        chooseRemaining:         wrap.dataset.i18nChooseRemaining || 'Choisissez encore {count} {label} avant d\'ajouter au panier.',
        chooseRemainingSingular: wrap.dataset.i18nChooseRemainingSingular || 'accessoire offert',
        chooseRemainingPlural:   wrap.dataset.i18nChooseRemainingPlural || 'accessoires offerts',
        chooseRemainingSingularNoBijou: wrap.dataset.i18nChooseRemainingSingularNoBijou || 'accessoire offert (hors bijou)',
        chooseRemainingPluralNoBijou:   wrap.dataset.i18nChooseRemainingPluralNoBijou || 'accessoires offerts (hors bijou)',
      };
      this.typeLabels = {
        bijoux:            wrap.dataset.i18nTypeBijoux || 'Bijoux',
        noeuds:            wrap.dataset.i18nTypeNoeuds || 'Nœuds',
        'mini-noeuds':     wrap.dataset.i18nTypeMiniNoeuds || 'Mini-Nœuds',
        pompons:           wrap.dataset.i18nTypePompons || 'Pompons',
        'triple-pompons':  wrap.dataset.i18nTypeTriplePompons || 'Triple Pompons',
        passementeries:    wrap.dataset.i18nTypePassementeries || 'Passementeries',
      };
      this.pairLabels = getPairLabels();

      this.grid           = wrap.querySelector('[id^="chatelles-acc-grid-"]');
      this.recap          = wrap.querySelector('.chatelles-acc-recap');
      this.recapItems     = wrap.querySelector('.chatelles-acc-recap-items');
      this.recapTotal     = wrap.querySelector('[id^="chatelles-acc-recap-total-price-"]');
      this.quotaBadge     = wrap.querySelector('.chatelles-acc-quota-current');
      this.tabsContainer  = wrap.querySelector('[id^="chatelles-acc-tabs-"]');
      this.tabs           = [];
      this.previewWrap    = wrap.querySelector('.chatelles-acc-preview');
      this.previewOverlayG = wrap.querySelector('[id^="chatelles-acc-preview-overlay-g"]');
      this.previewOverlayD = wrap.querySelector('[id^="chatelles-acc-preview-overlay-d"]');
      this.messageEl   = wrap.querySelector('.chatelles-acc-message');
      this.messageText = wrap.querySelector('[id^="chatelles-acc-message-text-"]');
      this.bijouNote   = wrap.querySelector('.chatelles-acc-bijou-note');

      this.selected = [];
      this.allProducts = [];

      this.loadProducts();
    }

    // ── Chargement ──────────────────────────────────────────────────────────

    async loadProducts() {
      try {
        this.allProducts = await fetchAllProducts(COLLECTION_HANDLE);
        const groups = groupProducts(this.allProducts);

        // Grille
        this.grid.innerHTML = buildGridHTML(groups, this.i18n, this.typeLabels, this.pairLabels);

        // Onglets dynamiques
        if (this.tabsContainer) {
          this.tabsContainer.innerHTML = buildTabsHTML(groups, this.typeLabels, this.i18n.tabAll);
        }

        // Afficher uniquement la première catégorie par défaut
        const activeTypes = TYPE_ORDER.filter(t => groups[t] && groups[t].length > 0);
        if (activeTypes.length > 1) {
          this.grid.querySelectorAll('.chatelles-acc-group').forEach((g, i) => {
            g.style.display = i === 0 ? '' : 'none';
          });
        }
        if (activeTypes.length > 0) this.updateGroupTitles(activeTypes[0]);
      } catch (e) {
        this.grid.innerHTML = `<p class="chatelles-acc-empty" style="color:#c00">${this.i18n.errorLoading}</p>`;
        console.error('[Chatelles] Erreur chargement accessoires:', e);
      }

      this.init();
    }

    // ── Init listeners ──────────────────────────────────────────────────────

    init() {
      // Onglets — rebind après génération dynamique
      this.tabs = this.tabsContainer ? Array.from(this.tabsContainer.querySelectorAll('.chatelles-acc-tab')) : [];
      this.tabs.forEach(tab => {
        tab.addEventListener('click', () => this.filterByType(tab.dataset.type, tab));
      });

      // Délégation sur la grille entière
      this.grid.addEventListener('click', (e) => {
        const item = e.target.closest('.chatelles-acc-item');
        if (item) this.toggleItem(item);
      });

      // Sync variante taille — Savor/Horizon émet 'variant:update' avec
      // e.detail.resource (le variant). On garde aussi les anciens noms au cas où.
      const syncVariant = (id) => {
        if (!id) return;
        this.productVariantId = String(id);
        this.wrap.dataset.productVariant = this.productVariantId;
      };
      document.addEventListener('variant:update', (e) => {
        syncVariant(e.detail?.resource?.id);
      });
      document.addEventListener('variant:selected', (e) => {
        syncVariant(e.detail?.resource?.id);
      });
      document.addEventListener('variant:changed', (e) => {
        syncVariant(e.detail?.variant?.id);
      });
      document.addEventListener('variantChange', (e) => {
        syncVariant(e.detail?.variant?.id);
      });

      this.updateUI();
    }

    // ── Filtrage par onglet ─────────────────────────────────────────────────

    filterByType(type, activeTab) {
      this.tabs.forEach(t => { t.classList.remove('active'); t.setAttribute('aria-selected', 'false'); });
      activeTab.classList.add('active');
      activeTab.setAttribute('aria-selected', 'true');

      // Afficher/masquer les groupes
      this.grid.querySelectorAll('.chatelles-acc-group').forEach(group => {
        group.style.display = (type === 'tous' || group.dataset.group === type) ? '' : 'none';
      });
      this.updateGroupTitles(type);
    }

    // Vue « Tous » → libellés courts (Bijoux, Nœuds…) ; une seule catégorie
    // → « Paire de bijoux », « Paire de nœuds »…
    updateGroupTitles(type) {
      const usePair = type !== 'tous';
      this.grid.querySelectorAll('.chatelles-acc-group-title').forEach(h => {
        const label = usePair ? h.dataset.labelPair : h.dataset.labelShort;
        if (label) h.textContent = label;
      });
    }

    // ── Toggle sélection ────────────────────────────────────────────────────

    toggleItem(item) {
      // Accessoire épuisé → non-sélectionnable
      if (item.dataset.accSoldout === '1') return;
      const id  = item.dataset.accId;
      const idx = this.selected.findIndex(s => s.id === id);

      if (idx > -1) {
        this.selected.splice(idx, 1);
        item.setAttribute('aria-pressed', 'false');
        item.classList.remove('selected');
      } else {
        this.selected.push({
          id,
          variantId: item.dataset.accVariant,
          title:     item.dataset.accTitle,
          price:     parseInt(item.dataset.accPrice, 10),
          type:      item.dataset.accType,
          image:     item.dataset.accImage,
          overlay:   item.dataset.accOverlay,
        });
        item.setAttribute('aria-pressed', 'true');
        item.classList.add('selected');
      }

      this.updateUI();
    }

    // ── Règles quota ────────────────────────────────────────────────────────

    // Règle générale (tout quota > 0) : N accessoires offerts, dont 1 bijou
    // maximum. Le 1er bijou sélectionné prend le « slot bijou » offert ; les
    // bijoux suivants restent payants même s'il reste des slots non-bijoux.
    getEffectivePrice(acc, idx) {
      if (this.quota === 0) return acc.price;
      let freeCount = 0, freeBijou = 0;
      for (let i = 0; i < idx; i++) {
        if (freeCount >= this.quota) break;
        const s = this.selected[i];
        if (s.type === 'bijoux') { if (freeBijou < 1) { freeBijou++; freeCount++; } }
        else { freeCount++; }
      }
      if (freeCount >= this.quota) return acc.price;
      if (acc.type === 'bijoux' && freeBijou >= 1) return acc.price;
      return 0;
    }

    countOfferts() {
      if (this.quota === 0) return 0;
      let count = 0, bijouOffert = 0;
      for (const acc of this.selected) {
        if (count >= this.quota) break;
        if (acc.type === 'bijoux') { if (bijouOffert < 1) { bijouOffert++; count++; } }
        else { count++; }
      }
      return count;
    }

    // ── UI ──────────────────────────────────────────────────────────────────

    updateUI() {
      this.updateQuotaBadge();
      this.updateOverlay();
      this.updateItemPrices();
      this.updateRecap();
      this.updateMessage();
      this.updateBijouNote();
      this.syncFormProperties();
    }

    // Note contextuelle : visible dès qu'un bijou de la sélection est payant
    // (limite « 1 bijou offert » atteinte).
    updateBijouNote() {
      if (!this.bijouNote) return;
      const hasPaidBijou = this.quota > 0 && this.selected.some(
        (s, i) => s.type === 'bijoux' && this.getEffectivePrice(s, i) > 0
      );
      this.bijouNote.classList.toggle('visible', hasPaidBijou);
    }

    updateQuotaBadge() {
      if (this.quotaBadge) this.quotaBadge.textContent = this.countOfferts();
    }

    // Vérifie si le quota obligatoire est atteint (les inclus sont tous choisis)
    isQuotaMet() {
      if (this.quota === 0) return true; // pas d'inclus → toujours OK
      return this.countOfferts() >= this.quota;
    }

    updateMessage() {
      if (!this.messageEl || !this.messageText) return;
      if (this.isQuotaMet()) {
        this.messageEl.classList.remove('visible');
        return;
      }
      const restant = this.quota - this.countOfferts();
      // Si le bijou offert est déjà pris, les offerts restants ne peuvent être
      // que des non-bijoux → on le précise pour lever l'ambiguïté.
      const bijouSlotUsed = this.selected.some(
        (s, i) => s.type === 'bijoux' && this.getEffectivePrice(s, i) === 0
      );
      let label;
      if (bijouSlotUsed) {
        label = restant === 1 ? this.i18n.chooseRemainingSingularNoBijou : this.i18n.chooseRemainingPluralNoBijou;
      } else {
        label = restant === 1 ? this.i18n.chooseRemainingSingular : this.i18n.chooseRemainingPlural;
      }
      this.messageText.textContent = this.i18n.chooseRemaining
        .replace('{count}', restant)
        .replace('{label}', label);
    }

    updateOverlay() {
      if (this.selected.length === 0) {
        if (this.previewOverlayG) this.previewOverlayG.style.display = 'none';
        if (this.previewOverlayD) this.previewOverlayD.style.display = 'none';
        if (this.previewWrap)    this.previewWrap.style.display = 'none';
        return;
      }
      // Dernier accessoire sélectionné sur les deux pieds
      const last = this.selected[this.selected.length - 1];
      if (this.previewOverlayG && last.overlay) {
        this.previewOverlayG.src = last.overlay;
        this.previewOverlayG.alt = last.title;
        this.previewOverlayG.style.display = '';
      }
      if (this.previewOverlayD && last.overlay) {
        this.previewOverlayD.src = last.overlay;
        this.previewOverlayD.alt = last.title;
        this.previewOverlayD.style.display = '';
      }
      if (this.previewWrap) this.previewWrap.style.display = '';
    }

    updateItemPrices() {
      const totalOfferts = this.countOfferts();
      let freeBijouUsed = 0;
      this.selected.forEach((s, i) => {
        if (s.type === 'bijoux' && this.getEffectivePrice(s, i) === 0) freeBijouUsed++;
      });

      this.grid.querySelectorAll('.chatelles-acc-item').forEach(item => {
        const id = item.dataset.accId;
        if (this.selected.find(s => s.id === id)) return;
        if (item.dataset.accSoldout === '1') return; // garder le libellé « Épuisé »
        const price   = parseInt(item.dataset.accPrice, 10);
        const accType = (item.dataset.accType || '').toLowerCase();
        const priceEl = item.querySelector('.chatelles-acc-item-price');
        if (!priceEl) return;
        // Un bijou offert déjà pris → les autres bijoux repassent à leur prix
        const wouldBeFree = totalOfferts < this.quota
          && !(accType === 'bijoux' && freeBijouUsed >= 1);
        if (wouldBeFree) {
          priceEl.textContent = this.i18n.free;
          priceEl.classList.add('free');
        } else {
          priceEl.textContent = price === 0 ? this.i18n.free : formatPrice(price);
          priceEl.classList.toggle('free', price === 0);
        }
      });
    }

    updateRecap() {
      if (this.selected.length === 0) {
        if (this.recap) this.recap.style.display = 'none';
        return;
      }
      if (this.recap) this.recap.style.display = '';

      let totalExtra = 0, html = '';
      this.selected.forEach((acc, i) => {
        const ep = this.getEffectivePrice(acc, i);
        totalExtra += ep;
        html += `<div class="chatelles-recap-item">
          <img src="${acc.image}" alt="${esc(acc.title)}" width="30" height="30">
          <span class="chatelles-recap-item-title">${esc(acc.title)}</span>
          <span class="chatelles-recap-item-price ${ep === 0 ? 'free' : ''}">${ep === 0 ? this.i18n.free : formatPrice(ep)}</span>
          <button class="chatelles-recap-remove" data-id="${acc.id}" aria-label="${this.i18n.remove}">×</button>
        </div>`;
      });

      if (this.recapItems) this.recapItems.innerHTML = html;
      if (this.recapTotal) this.recapTotal.textContent = totalExtra === 0 ? this.i18n.free : formatPrice(totalExtra);

      this.recap.querySelectorAll('.chatelles-recap-remove').forEach(btn => {
        btn.addEventListener('click', (e) => {
          e.stopPropagation();
          const gridItem = this.grid.querySelector(`[data-acc-id="${btn.dataset.id}"]`);
          if (gridItem) this.toggleItem(gridItem);
        });
      });
    }

    syncFormProperties() {
      const form = document.querySelector('form[action*="/cart/add"]');
      if (!form) return;
      form.querySelectorAll('[data-chatelles-acc]').forEach(el => el.remove());
      if (this.selected.length > 0) {
        const input = document.createElement('input');
        input.type  = 'hidden';
        input.name  = 'properties[Accessoires]';
        input.value = this.selected.map(s => s.title).join(', ');
        input.setAttribute('data-chatelles-acc', '1');
        form.appendChild(input);
      }
    }

    // ── Ajout au panier groupé ──────────────────────────────────────────────

    // Résout la variante actuellement sélectionnée. L'input[name="id"] du
    // formulaire /cart/add est la source de vérité : Savor le met à jour à
    // chaque changement de taille (product-form.js). On retombe sinon sur la
    // valeur suivie via les events, puis sur la variante initiale du bloc.
    getSelectedVariantId() {
      const form = document.querySelector('form[action*="/cart/add"]');
      const input = form && form.querySelector('input[name="id"]');
      if (input && input.value) return input.value;
      return this.productVariantId || this.wrap.dataset.productVariant || null;
    }

    // Affiche un message bloquant dans la barre existante (réutilisée du quota)
    // et attire l'attention (scroll + shake).
    showBlockingMessage(text) {
      if (!this.messageEl || !this.messageText) return;
      this.messageText.textContent = text;
      this.messageEl.classList.add('visible');
      this.messageEl.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
      this.wrap.classList.add('chatelles-shake');
      setTimeout(() => this.wrap.classList.remove('chatelles-shake'), 500);
    }

    async addAllToCart({ openDrawer = true } = {}) {
      // Variante réellement sélectionnée : on lit d'abord l'input du formulaire
      // produit (mis à jour par Savor à chaque changement de taille), sinon on
      // retombe sur la variante suivie via les events / la valeur initiale.
      const parentVariantId = this.getSelectedVariantId();
      if (!parentVariantId) return false;

      const items = [{
        id: parseInt(parentVariantId, 10),
        quantity: 1,
        properties: { '_accessoires': this.selected.map(s => s.title).join(', ') || '' }
      }];

      this.selected.forEach((acc, i) => {
        const ep = this.getEffectivePrice(acc, i);
        items.push({
          id: parseInt(acc.variantId, 10),
          quantity: 1,
          properties: {
            'Avec': this.productTitle,
            '_variant_parent': parentVariantId,
            ...(ep === 0 ? { '_offert': 'oui', '_prix_original': formatPrice(acc.price) } : {}),
          }
        });
      });

      try {
        const res = await fetch('/cart/add.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ items }),
        });
        if (!res.ok) {
          // Shopify renvoie un JSON { status, message, description } — souvent
          // « ... is out of stock » quand un accessoire vient de passer en rupture.
          let detail = '';
          try { detail = (await res.json())?.description || ''; } catch (_) {}
          this.showBlockingMessage(detail || this.i18n.errorAdd);
          throw new Error('cart/add failed: ' + (detail || res.status));
        }
        await res.json();

        // ── Savor / Horizon AI : rafraîchir + ouvrir le drawer ──────────────
        if (openDrawer) {
          try {
            const cartData = await fetch('/cart.js').then(r => r.json());
            const { CartUpdateEvent } = await import('@theme/events');
            const cartDrawer = document.querySelector('cart-drawer-component');
            const event = new CartUpdateEvent(cartData, 'manual-trigger', {
              itemCount: cartData.item_count,
              source: 'chatelles-accessoires',
              sections: {},
            });
            document.dispatchEvent(event);
            if (cartDrawer) {
              if (typeof cartDrawer.open === 'function') {
                cartDrawer.open();
              } else {
                cartDrawer.setAttribute('open', '');
              }
            }
          } catch (horizonErr) {
            console.warn('[Chatelles] CartUpdateEvent non disponible, fallback:', horizonErr);
            const cartData = await fetch('/cart.js').then(r => r.json());
            document.dispatchEvent(new CustomEvent('cart:updated', { detail: cartData }));
            document.dispatchEvent(new CustomEvent('cart:open', { bubbles: true }));
            const cartBtn = document.querySelector('[aria-controls="cart-drawer-component"], [aria-controls="CartDrawer"], .js-cart-open, [data-drawer-open="cart"]');
            if (cartBtn) cartBtn.click();
          }
        }

        return true;
      } catch (err) {
        console.error('[Chatelles accessoires] Erreur panier:', err);
        return false;
      }
    }
  }

  // ── Intercepter "Ajouter au panier" ──────────────────────────────────────
  //
  // Problème : Savor écoute le clic sur le bouton en plus du submit.
  // stopImmediatePropagation sur 'submit' ne bloque pas son listener de clic.
  // Solution : on intercepte le CLIC sur le bouton add en phase capture,
  // on le bloque immédiatement, puis on gère nous-mêmes l'ajout.

  function interceptAddToCart() {

    // Garde anti-double-ajout : un seul ajout à la fois
    let _adding = false;

    async function handleAddIntent(e) {
      const form = e.target.closest && e.target.closest('form[action*="/cart/add"]');
      if (!form) return;

      const wrap = document.querySelector('.chatelles-acc-wrap');
      if (!wrap?._accSelector) return;
      const selector = wrap._accSelector;

      // Quota = 0 et aucun accessoire sélectionné → laisser Savor gérer
      if (selector.quota === 0 && selector.selected.length === 0) return;

      // Bloquer TOUT
      e.preventDefault();
      e.stopImmediatePropagation();
      e.stopPropagation();

      // Anti-double
      if (_adding) return;

      // Quota non atteint → bloquer et afficher le message
      if (!selector.isQuotaMet()) {
        if (selector.messageEl && selector.messageText) {
          selector.messageEl.classList.add('visible');
          selector.messageEl.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
          selector.wrap.classList.add('chatelles-shake');
          setTimeout(() => selector.wrap.classList.remove('chatelles-shake'), 500);
        }
        return;
      }

      // Quota atteint (ou quota=0 avec accessoires) → on prend la main
      const btn = form.querySelector('[name="add"], [type="submit"]');
      _adding = true;
      if (btn) {
        btn.disabled = true;
        const orig = btn.textContent;
        btn.textContent = selector.i18n.adding;

        const ok = await selector.addAllToCart();

        if (ok) {
          btn.textContent = selector.i18n.added;
          // addAllToCart gère déjà l'ouverture du drawer Savor
        } else {
          btn.textContent = selector.i18n.errorRetry;
        }

        setTimeout(() => {
          btn.textContent = orig;
          btn.disabled = false;
          _adding = false;
        }, 1400);
      } else {
        _adding = false;
      }
    }

    // 1. Intercepter le submit (phase capture = avant Savor)
    document.addEventListener('submit', handleAddIntent, true);

    // 2. Intercepter le clic sur le bouton add (Savor peut bypasser submit)
    document.addEventListener('click', (e) => {
      const btn = e.target.closest('[name="add"], [data-add-to-cart], .product-form__submit');
      if (!btn) return;
      const form = btn.closest('form[action*="/cart/add"]');
      if (!form) return;
      const wrap = document.querySelector('.chatelles-acc-wrap');
      if (!wrap?._accSelector) return;
      const selector = wrap._accSelector;
      if (selector.quota === 0 && selector.selected.length === 0) return;
      handleAddIntent(e);
    }, true);
  }

  // ── Intercepter "Acheter maintenant" (accelerated checkout) ──────────────
  //
  // Les boutons Shop Pay / Apple Pay / Google Pay utilisent payment_button et
  // soumettent directement sans passer par le panier. On intercepte le clic
  // sur leur conteneur, on ajoute tout au panier, puis on redirige vers /checkout.

  function interceptBuyNow() {
    let _buying = false;

    document.addEventListener('click', async (e) => {
      const accBlock = e.target.closest('.accelerated-checkout-block');
      if (!accBlock) return;

      const wrap = document.querySelector('.chatelles-acc-wrap');
      if (!wrap?._accSelector) return;
      const selector = wrap._accSelector;

      // Quota = 0 et aucun accessoire → laisser Shopify gérer nativement
      if (selector.quota === 0 && selector.selected.length === 0) return;

      e.preventDefault();
      e.stopImmediatePropagation();
      e.stopPropagation();

      if (_buying) return;

      // Quota non atteint → bloquer et afficher le message
      if (!selector.isQuotaMet()) {
        if (selector.messageEl && selector.messageText) {
          selector.messageEl.classList.add('visible');
          selector.messageEl.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
          selector.wrap.classList.add('chatelles-shake');
          setTimeout(() => selector.wrap.classList.remove('chatelles-shake'), 500);
        }
        return;
      }

      // Quota atteint → ajouter au panier et aller au checkout
      _buying = true;
      const btns = accBlock.querySelectorAll('button, [role="button"]');
      btns.forEach(b => { b.disabled = true; });

      const ok = await selector.addAllToCart({ openDrawer: false });

      if (ok) {
        window.location.href = '/checkout';
      } else {
        btns.forEach(b => { b.disabled = false; });
        _buying = false;
      }
    }, true);
  }

  // ── Init ──────────────────────────────────────────────────────────────────

  function initAll() {
    document.querySelectorAll('.chatelles-acc-wrap:not([data-acc-init])').forEach(wrap => {
      wrap.setAttribute('data-acc-init', '1');
      wrap._accSelector = new AccessoiresSelector(wrap);
    });
  }

  document.addEventListener('DOMContentLoaded', () => { initAll(); interceptAddToCart(); interceptBuyNow(); });
  document.addEventListener('shopify:section:load', initAll);

})();
